import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, m } from 'motion/react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { MODAL_OVERLAY, MODAL_TARJETA } from '@/lib/motion';

// Modal centrado con la misma API que <BottomSheet> (título, ícono, contenido
// con scroll y footer fijo). Se cierra con el fondo, la X o Escape.

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

export function Modal({ open, onClose, title, subtitle, icon, children, footer, className }: ModalProps) {
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <m.div
          key="modal-overlay"
          {...MODAL_OVERLAY}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{
            backgroundColor: 'rgba(0, 30, 64, 0.45)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))',
            paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))',
          }}
          onClick={onClose}
        >
          <m.div
            {...MODAL_TARJETA}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className={cn(
              'relative w-full max-w-md max-h-[85vh] bg-white flex flex-col overflow-hidden rounded-[24px]',
              className,
            )}
            style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.22)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            {title && (
              <div
                className="flex items-center gap-3 px-5 py-4 shrink-0"
                style={{ borderBottom: '1px solid rgba(60,60,67,0.13)' }}
              >
                {icon && (
                  <div
                    className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0"
                    style={{ backgroundColor: '#001e40' }}
                  >
                    <span style={{ color: '#fed65b' }}>{icon}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-ios-headline text-sgcc-primary truncate">{title}</p>
                  {subtitle && (
                    <p className="text-ios-caption1 text-sgcc-on-surface-variant truncate mt-0.5">{subtitle}</p>
                  )}
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 tap-active"
                  style={{ backgroundColor: 'rgba(60,60,67,0.1)' }}
                  aria-label="Cerrar"
                >
                  <X size={16} strokeWidth={2.5} style={{ color: '#43474f' }} />
                </button>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain">{children}</div>

            {/* Footer */}
            {footer && (
              <div className="shrink-0 px-4 pt-3 pb-4" style={{ borderTop: '1px solid rgba(60,60,67,0.1)' }}>
                {footer}
              </div>
            )}
          </m.div>
        </m.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
